import type { RowSnapshot } from '@pf/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { CalendarPlus, Download, FileText, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../api.ts';
import { useMe, useTrip } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import { Button } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { backup, download, expensesCSV, gpx, ics, kml, restoreEdits, slug } from '../model/exports.ts';
import s from './Export.module.css';

/** F-13 on the web: the same files as the apps, made in the browser from the trip's rows. */
export function Export() {
  const { tripId } = useParams({ from: '/app/trips/$tripId/export' });
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const queries = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model || !me.data) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const people: Record<string, string> = { [me.data.user.id]: me.data.user.displayName || 'Me' };
  if (me.data.partner) people[me.data.partner.id] = me.data.partner.displayName || 'Partner';
  const name = slug(model.title);
  const rows = () => queries.getQueryData<RowSnapshot[]>(['bundle', tripId]) ?? [];

  const subscribe = async () => {
    try {
      const { url } = await api<{ url: string }>(`/calendar/feed-url?trip=${tripId}`);
      await navigator.clipboard.writeText(url).catch(() => undefined);
      setMessage(
        'The calendar link is copied. Add it as a subscribed calendar; webcal: opens it in your calendar app.',
      );
      window.location.href = url.replace(/^https?:/, 'webcal:');
    } catch {
      setMessage('Subscribing needs a connection.');
    }
  };

  const restore = async (f: File) => {
    setBusy(true);
    try {
      const chunks = restoreEdits(JSON.parse(await f.text()));
      let refused = 0;
      let n = 0;
      for (const chunk of chunks) {
        refused += await save(chunk);
        n += chunk.length;
      }
      setMessage(`Restored ${n - refused} rows${refused ? `; ${refused} were refused` : ''}.`);
    } catch (e) {
      setMessage(e instanceof SyntaxError ? "That file isn't a Pathfinder backup." : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const rowsOf: [string, string, () => void][] = [
    [
      'Expenses CSV',
      'Every expense with PHP and EUR',
      () => download(`${name}-expenses.csv`, expensesCSV(model, people), 'text/csv'),
    ],
    [
      'Calendar (ICS)',
      'Bookings and timed stops in local time',
      () => download(`${name}.ics`, ics(model), 'text/calendar'),
    ],
    [
      'Places (KML)',
      "Every day's stops and the lists",
      () => download(`${name}.kml`, kml(model), 'application/vnd.google-earth.kml+xml'),
    ],
    [
      'My track (GPX)',
      'Synced, simplified segments (the iPhone exports raw fixes)',
      () => download(`${name}-track.gpx`, gpx(model, me.data.user.id), 'application/gpx+xml'),
    ],
  ];

  return (
    <div className={s.page}>
      <header className={s.top}>
        <div className={cx(t.readoutS, s.muted)}>{`// ${model.title}`}</div>
        <h1 className={cx(t.displayL, s.flat)}>Export</h1>
      </header>
      {message && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {message}
        </p>
      )}
      <section aria-labelledby="pf-export">
        <h2 id="pf-export" className={cx(t.label, s.head)}>
          Export
        </h2>
        <ul className={s.list}>
          <li>
            <div>
              <div className={t.titleM}>Pre-trip document</div>
              <div className={cx(t.caption, s.muted)}>
                Itinerary, bookings and addresses; print it or save it as PDF
              </div>
            </div>
            <Link to="/trips/$tripId/print" params={{ tripId }} className={cx(s.link, t.bodyS)}>
              <FileText size={16} aria-hidden="true" /> Open
            </Link>
          </li>
          {rowsOf.map(([title, detail, make]) => (
            <li key={title}>
              <div>
                <div className={t.titleM}>{title}</div>
                <div className={cx(t.caption, s.muted)}>{detail}</div>
              </div>
              <Button
                variant="ghost"
                size="small"
                icon={Download}
                onClick={make}
                aria-label={`Download ${title}`}
              >
                Download
              </Button>
            </li>
          ))}
          <li>
            <div>
              <div className={t.titleM}>Subscribe in Calendar</div>
              <div className={cx(t.caption, s.muted)}>
                A live feed: changes reach your calendar by themselves
              </div>
            </div>
            <Button variant="ghost" size="small" icon={CalendarPlus} onClick={() => void subscribe()}>
              Subscribe
            </Button>
          </li>
        </ul>
      </section>
      <section aria-labelledby="pf-backup">
        <h2 id="pf-backup" className={cx(t.label, s.head)}>
          Backup
        </h2>
        <ul className={s.list}>
          <li>
            <div>
              <div className={t.titleM}>Backup (JSON)</div>
              <div className={cx(t.caption, s.muted)}>Every row of the trip; the apps restore it too</div>
            </div>
            <Button
              variant="ghost"
              size="small"
              icon={Download}
              aria-label="Download Backup (JSON)"
              onClick={() =>
                download(
                  `${name}-backup.json`,
                  JSON.stringify(backup(rows(), tripId), null, 2),
                  'application/json',
                )
              }
            >
              Download
            </Button>
          </li>
          <li>
            <div>
              <div className={t.titleM}>Restore from a backup</div>
              <div className={cx(t.caption, s.muted)}>
                Merges the backup into the trip; it syncs like any edit
              </div>
            </div>
            <input
              ref={file}
              type="file"
              accept="application/json,.json"
              hidden
              aria-label="Backup file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void restore(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="small"
              icon={RotateCcw}
              disabled={busy}
              onClick={() => file.current?.click()}
            >
              {busy ? 'Restoring…' : 'Restore'}
            </Button>
          </li>
        </ul>
      </section>
    </div>
  );
}
